import { DateRange, LocaleCodeToDateFormatMap, LocaleCodeToTimeFormatMap } from '@doss/common-all'
import dayjs, { Dayjs } from 'dayjs'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { ApplicationOrientation } from '~/common/generated/apollo-operations'
import { useApplicationOrientation } from '~/common/hooks/useApplicationOrientation'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { Modal } from '~/components/ui/molecules/modal/Modal'
import { InputWrapper } from '../../base/input-wrapper'
import { BaseInputProps } from '../../base/types'
import { DateRangeContent } from './date-range-picker-content'
import { DateRangePickerClearButton, DateRangePickerInput } from './date-range-picker-controls'

export interface DateRangePickerProps extends Omit<BaseInputProps, 'placeholder'> {
  allowClear?: boolean
  onChange: (value?: DateRange) => void
  useModal?: boolean
  value?: DateRange
  isBorderless?: boolean
  /**
   * Placeholder options:
   * - string: Custom placeholder text (will show for both start and end dates)
   * - 'format': Show date format as placeholder (e.g. "MM/DD/YYYY → MM/DD/YYYY")
   * - false/undefined: No placeholder
   */
  placeholder?: string | 'format' | false
  /** When true, shows time inputs in addition to date inputs */
  isDateTime?: boolean
}

export function DateRangePicker({
  allowClear = false,
  disabled,
  wrapperClassName,
  errorText,
  helperText,
  label,
  required,
  showDirtyIndicator,
  tooltip,
  onChange,
  useModal = false,
  value,
  isBorderless = false,
  placeholder,
  readOnly,
  isDateTime,
}: DateRangePickerProps) {
  const { locale } = useGlobalContext()
  const { orientation } = useApplicationOrientation()

  const dateFormat = LocaleCodeToDateFormatMap[locale]
  const timeFormat = LocaleCodeToTimeFormatMap[locale]

  const [isOpen, setIsOpen] = useState(false) // State to control popover visibility
  const [dateRange, setDateRange] = useState<DateRange | undefined>(value)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(() => {
    if (value?.from) {
      return value.from
    }
    if (value?.to) {
      return value.to
    }
    return dayjs()
  })

  // Sync internal state with external value
  useEffect(() => {
    setDateRange(value)
    if (value?.from) {
      setCurrentMonth(value.from)
    } else if (value?.to) {
      setCurrentMonth(value.to)
    }
  }, [value])

  // Format dates for display (include time for DateTime columns)
  const startDateString = dateRange?.from
    ? isDateTime
      ? dateRange.from.format(`${dateFormat} ${timeFormat}`)
      : dateRange.from.format(dateFormat)
    : ''
  const endDateString = dateRange?.to
    ? isDateTime
      ? dateRange.to.format(`${dateFormat} ${timeFormat}`)
      : dateRange.to.format(dateFormat)
    : ''

  // Handle both text input and calendar selection
  const handleRangeChange = (range: DateRange) => {
    // Only proceed if we have either a from or to date
    if (range.from || range.to) {
      // Update the internal date range state
      setDateRange(range)

      // Get the values that changed by comparing the new range with previous state
      const fromChanged = Boolean(
        range.from && (!dateRange?.from || !range.from.isSame(dateRange.from, 'day')),
      )
      const toChanged = Boolean(
        range.to && (!dateRange?.to || !range.to.isSame(dateRange.to, 'day')),
      )

      // If the to date changed (either by typing or clicking), show its month
      if (toChanged && range.to) {
        setCurrentMonth(range.to)
      }
      // If the from date changed (either by typing or clicking), show its month
      else if (fromChanged && range.from) {
        setCurrentMonth(range.from)
      }

      onChange(range)
    }
  }

  const handleMonthChange = (newMonth: Date) => {
    setCurrentMonth(dayjs(newMonth))
  }

  const handleDoneClick = () => {
    setIsOpen(false)
  }

  const getDisplayValue = () => {
    // If dates are selected, show them with dateFormat fallback for missing values
    if (dateRange?.from || dateRange?.to) {
      return (
        <span className="flex items-center gap-1">
          <span className={!startDateString ? 'text-gray-8' : ''}>
            {startDateString || dateFormat}
          </span>
          {orientation === ApplicationOrientation.RIGHT_TO_LEFT ? (
            <ArrowLeft className="h-3 w-3 flex-shrink-0" />
          ) : (
            <ArrowRight className="h-3 w-3 flex-shrink-0" />
          )}
          <span className={!endDateString ? 'text-gray-8' : ''}>{endDateString || dateFormat}</span>
        </span>
      )
    }

    // If no dates are selected, show placeholder
    if (!disabled && !readOnly && placeholder) {
      if (placeholder === 'format') {
        // Show format with arrow for 'format' placeholder
        return (
          <span className="flex items-center gap-1 text-gray-8">
            <span>{dateFormat}</span>
            {orientation === ApplicationOrientation.RIGHT_TO_LEFT ? (
              <ArrowLeft className="h-3 w-3 flex-shrink-0" />
            ) : (
              <ArrowRight className="h-3 w-3 flex-shrink-0" />
            )}
            <span>{dateFormat}</span>
          </span>
        )
      } else {
        // Show custom placeholder text without arrow
        return <span className="text-gray-8">{placeholder}</span>
      }
    }

    return ''
  }

  const dateRangeContentProps = {
    currentMonth,
    dateFormat,
    disabled,
    endDateString,
    error: errorMessage,
    onDoneClick: handleDoneClick,
    onMonthChange: handleMonthChange,
    onRangeChange: handleRangeChange,
    setError: setErrorMessage,
    startDateString,
    dateRange,
    isDateTime,
  }

  // If useModal is true, render editor as a full screen modal
  if (useModal) {
    const content = (
      <div className="group/date-range-picker relative">
        <DateRangePickerInput
          disabled={disabled}
          value={dateRange}
          formatDateDisplay={getDisplayValue}
          onClick={() => setIsOpen(true)}
          isOpen={isOpen}
          isBorderless={isBorderless}
          readOnly={readOnly}
        />
        {allowClear && !disabled && dateRange && <DateRangePickerClearButton onChange={onChange} />}
      </div>
    )

    return (
      <>
        {!isBorderless ? (
          <InputWrapper
            wrapperClassName={wrapperClassName}
            errorText={errorText}
            helperText={helperText}
            label={label}
            required={required}
            showDirtyIndicator={showDirtyIndicator}
            tooltip={tooltip}
          >
            {content}
          </InputWrapper>
        ) : (
          content
        )}

        <Modal onClose={() => setIsOpen(false)} open={isOpen}>
          <DateRangeContent {...dateRangeContentProps} />
        </Modal>
      </>
    )
  }

  // Otherwise, render editor as a popover
  const popoverContent = (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <div className="group/date-range-picker relative">
        <PopoverTrigger asChild>
          <DateRangePickerInput
            disabled={disabled}
            value={dateRange}
            formatDateDisplay={getDisplayValue}
            onClick={() => setIsOpen(true)}
            isOpen={isOpen}
            isBorderless={isBorderless}
            readOnly={readOnly}
          />
        </PopoverTrigger>
        {allowClear && !disabled && dateRange && <DateRangePickerClearButton onChange={onChange} />}
      </div>
      <PopoverContent
        align="start"
        className={cn('z-[1301] p-0', isDateTime ? 'w-[600px]' : 'w-auto')}
      >
        <DateRangeContent {...dateRangeContentProps} />
      </PopoverContent>
    </Popover>
  )

  return !isBorderless ? (
    <InputWrapper
      wrapperClassName={wrapperClassName}
      errorText={errorText}
      helperText={helperText}
      label={label}
      required={required}
      showDirtyIndicator={showDirtyIndicator}
      tooltip={tooltip}
    >
      {popoverContent}
    </InputWrapper>
  ) : (
    popoverContent
  )
}
